import type { ReactNode } from 'react';
import { Button } from '@mui/material';
import ExtensionRounded from '@mui/icons-material/ExtensionRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { Link as RouterLink } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { useAccess, type AccessRule } from './access';
import { useMe } from './useSession';

/**
 * Route guard for UX: explains why a page is unavailable (not in the plan,
 * missing role). The API enforces the same rule on every request.
 */
export function RequireAccess({ rule, children }: { rule: AccessRule; children: ReactNode }) {
  const can = useAccess();
  const me = useMe();
  if (can(rule)) return children;
  if (rule.module && me.entitlements.personal.includes(rule.module)) {
    return (
      <EmptyState
        icon={<ExtensionRounded />}
        title="Ative este módulo para usar"
        description="Ele está disponível para você neste espaço, sem custo. É só ativar em Módulos."
        action={
          <Button component={RouterLink} to="/modulos" variant="contained">
            Ir para Módulos
          </Button>
        }
      />
    );
  }
  const reason = rule.module || rule.feature ? 'plan' : 'role';
  return (
    <EmptyState
      icon={<LockOutlined />}
      title={reason === 'plan' ? 'Recurso fora do seu plano' : 'Você não tem acesso a esta área'}
      description={
        reason === 'plan'
          ? 'Este recurso não está incluído no plano deste espaço. Fale com o responsável para ativá-lo.'
          : 'Sua função neste espaço não inclui esta área.'
      }
      action={
        <Button component={RouterLink} to="/" variant="contained">
          Voltar para o início
        </Button>
      }
    />
  );
}
